import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/api/client";
import { errorMessage } from "@/api/errors";

interface Props {
  accountId: string;
  onClose: () => void;
}

interface SeedResult {
  campaigns: number;
  adsets: number;
  ads: number;
  errors: number;
  errorMessages: string[];
  createdCampaignIds: string[];
}

const OBJECTIVES = [
  "OUTCOME_TRAFFIC",
  "OUTCOME_AWARENESS",
  "OUTCOME_ENGAGEMENT",
  "OUTCOME_LEADS",
  "OUTCOME_SALES",
  "OUTCOME_APP_PROMOTION",
];

export default function MockSeedDialog({ accountId, onClose }: Props) {
  const qc = useQueryClient();
  const [campaigns, setCampaigns] = useState(5);
  const [adsetsPerCampaign, setAdsetsPerCampaign] = useState(3);
  const [adsPerAdset, setAdsPerAdset] = useState(0);   // default 0 (no page_id usually)
  const [namePrefix, setNamePrefix] = useState("MockGen");
  const [objective, setObjective] = useState("OUTCOME_TRAFFIC");
  const [dailyBudget, setDailyBudget] = useState(20);  // USD
  const [pageId, setPageId] = useState("");
  const [result, setResult] = useState<SeedResult | null>(null);

  const seed = useMutation({
    mutationFn: async () => {
      const body = {
        campaigns,
        adsetsPerCampaign,
        adsPerAdset,
        namePrefix,
        objective,
        dailyBudgetCents: Math.round(dailyBudget * 100),
        pageId: pageId || undefined,
      };
      const { data, error, response } = await api.POST(
        "/api/v1/ad-accounts/{accountId}/mock/seed" as never,
        { params: { path: { accountId } }, body } as never
      );
      if (error || !response.ok) throw error || new Error("Mock 失败");
      return data as unknown as SeedResult;
    },
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ["campaigns", accountId] });
      if (r.errors === 0) {
        toast.success(`已创建 ${r.campaigns} Campaign / ${r.adsets} AdSet / ${r.ads} Ad`);
      } else {
        toast.warning(`部分成功：${r.campaigns}C/${r.adsets}S/${r.ads}A，${r.errors} 个错误`);
      }
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const total = campaigns + campaigns * adsetsPerCampaign + campaigns * adsetsPerCampaign * adsPerAdset;
  const pageIdMissing = adsPerAdset > 0 && !pageId.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-background rounded-lg border border-border w-full max-w-lg shadow-lg max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="font-semibold">沙盒造数据</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          {!result && (
            <>
              <p className="text-muted-foreground text-xs">
                仅作用于沙盒账户。Campaign 默认 PAUSED，预算在 Campaign 级；
                AdSet 默认 LINK_CLICKS + 美国地区 + 18-65 岁；
                Ad 创建需要 <strong>Facebook Page</strong>，沙盒无 Page 时请把 Ad 数设为 0。
              </p>

              <div className="grid grid-cols-3 gap-3">
                <NumField label="Campaign 数" value={campaigns} setValue={setCampaigns} min={1} max={20} />
                <NumField label="每 C 下 AdSet" value={adsetsPerCampaign} setValue={setAdsetsPerCampaign} min={0} max={5} />
                <NumField label="每 S 下 Ad" value={adsPerAdset} setValue={setAdsPerAdset} min={0} max={5} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1.5">
                  <span className="text-xs font-medium">名称前缀</span>
                  <input
                    value={namePrefix}
                    onChange={(e) => setNamePrefix(e.target.value)}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  />
                </label>
                <label className="space-y-1.5">
                  <span className="text-xs font-medium">日预算 (USD)</span>
                  <input
                    type="number"
                    value={dailyBudget}
                    onChange={(e) => setDailyBudget(Number(e.target.value))}
                    min={1}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  />
                </label>
              </div>

              <label className="block space-y-1.5">
                <span className="text-xs font-medium">广告目标</span>
                <select
                  value={objective}
                  onChange={(e) => setObjective(e.target.value)}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                >
                  {OBJECTIVES.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>

              {adsPerAdset > 0 && (
                <label className="block space-y-1.5">
                  <span className="text-xs font-medium">Facebook Page ID（Ad 创建必填）</span>
                  <input
                    value={pageId}
                    onChange={(e) => setPageId(e.target.value)}
                    placeholder="例：1234567890"
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
                  />
                  {pageIdMissing ? (
                    <span className="text-xs text-rose-600">
                      必填：未填 pageId 时 Ad 全部静默跳过；要么填上，要么把"每 S 下 Ad"设为 0。
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      沙盒账户需先在 Meta Business 后台绑定一个测试 Page，从那里拿 page_id。
                    </span>
                  )}
                </label>
              )}

              <div className="bg-muted rounded-md px-3 py-2 text-xs text-muted-foreground">
                共将创建：<strong>{campaigns} Campaign + {campaigns * adsetsPerCampaign} AdSet + {campaigns * adsetsPerCampaign * adsPerAdset} Ad</strong>，
                调用 Meta API 约 <strong>{total + (adsPerAdset > 0 ? 1 : 0)}</strong> 次
              </div>
            </>
          )}

          {result && (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2 text-center">
                <Stat label="Campaign" value={result.campaigns} ok />
                <Stat label="AdSet" value={result.adsets} ok={result.adsets > 0} />
                <Stat label="Ad" value={result.ads} ok={result.ads > 0} />
                <Stat label="错误" value={result.errors} ok={result.errors === 0} bad={result.errors > 0} />
              </div>

              {result.errors > 0 && (
                <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs space-y-1 max-h-48 overflow-auto">
                  {result.errorMessages.map((e, i) => (
                    <div key={i} className="text-rose-900 break-words">{e}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
          {!result && (
            <button
              onClick={() => seed.mutate()}
              disabled={seed.isPending || pageIdMissing}
              className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {seed.isPending ? "造数据中…" : "开始造"}
            </button>
          )}
          {result && (
            <button
              onClick={() => setResult(null)}
              className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
            >
              再次造
            </button>
          )}
          <button onClick={onClose} className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted">
            {result ? "关闭" : "取消"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NumField({ label, value, setValue, min, max }: {
  label: string; value: number; setValue: (v: number) => void; min: number; max: number;
}) {
  return (
    <label className="space-y-1.5">
      <span className="text-xs font-medium">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => setValue(Math.max(min, Math.min(max, Number(e.target.value))))}
        min={min}
        max={max}
        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
      />
    </label>
  );
}

function Stat({ label, value, ok, bad }: { label: string; value: number; ok?: boolean; bad?: boolean }) {
  const cls = bad ? "bg-rose-50 text-rose-900" : ok ? "bg-emerald-50 text-emerald-900" : "bg-muted";
  return (
    <div className={`rounded-md ${cls} px-2 py-2`}>
      <div className="text-lg font-semibold">{value}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
}
